import React, { ReactNode, useCallback, useMemo } from "react";
import { connect } from "react-redux";

import MarkerShape from "@mapmycustomers/shared/enum/MarkerShape";
import MarkerSize from "@mapmycustomers/shared/enum/MarkerSize";
import { EntityPin } from "@mapmycustomers/shared/types/map";
import anyColorToHex from "@mapmycustomers/shared/util/color/anyColorToHex";
import getContrastColor from "@mapmycustomers/shared/util/color/getContrastColor";

import MarkerEffect from "@app/enum/MarkerEffect";
import { getMapSettings } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import colors from "@app/styles/_colors.module.scss";
import defaultEntityStyleGetter from "@app/util/map/markerStyles/defaultEntityStyleGetter";

import LocationMiniMap, { LocationMiniMapEntity, LocationMiniMapProps } from "./index";

interface RecordLocationMiniMapProps {
  before?: ReactNode;
  className?: string;
  draggable?: boolean;
  entity?: LocationMiniMapEntity;
  mapOptions?: google.maps.MapOptions;
  mapSettings: {
    markerSize: MarkerSize;
    showEntityIcons: boolean;
  };
  onDragPin?: (latLng: google.maps.LatLng) => void;
}

const RecordLocationMiniMap = ({
  before,
  className,
  draggable,
  entity,
  mapOptions,
  mapSettings,
  onDragPin,
}: RecordLocationMiniMapProps) => {
  const handleMouseUp = useCallback<NonNullable<LocationMiniMapProps["onMouseUp"]>>(
    (_, event: google.maps.MapMouseEvent) => {
      if (draggable && event.latLng) {
        onDragPin?.(event.latLng);
      }
    },
    [draggable, onDragPin]
  );

  const entityPinStyleGetter = useMemo(() => {
    const styleGetter = defaultEntityStyleGetter({
      colorGetter: () => {
        const fillColor = entity?.color ? anyColorToHex(entity.color) : colors.slate;
        return {
          color: getContrastColor(fillColor) === "black" ? colors.slate : colors.white,
          fillColor,
        };
      },
      draggable,
      effect: MarkerEffect.SHADOW,
      icon: () => (mapSettings.showEntityIcons ? entity?.entity : undefined),
      markerSize: mapSettings.markerSize,
      shape: () => entity?.shape ?? MarkerShape.SQUARE,
    });
    // FIXME: generalize defaultEntityStyleGetter so that we can use it with other types in addition to EntityPin
    // For now this is a fake call to style getter. We do not use given argument inside that style getter completely
    return () => styleGetter({} as EntityPin);
  }, [draggable, entity, mapSettings]);

  if (!entity?.geoPoint?.coordinates) {
    return null;
  }

  return (
    <LocationMiniMap
      before={before}
      className={className}
      coordinates={entity.geoPoint.coordinates}
      mapOptions={mapOptions}
      onMouseUp={handleMouseUp}
      styleGetter={entityPinStyleGetter}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  mapSettings: getMapSettings(state),
});

export default connect(mapStateToProps)(RecordLocationMiniMap);
